import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import { toast } from "sonner";

const StarRow = ({ value, onChange, size = "w-6 h-6", testidPrefix = "star" }) => (
  <div className="flex gap-1" data-testid={`${testidPrefix}-row`}>
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= value;
      const Cmp = onChange ? "button" : "span";
      return (
        <Cmp
          key={n}
          type={onChange ? "button" : undefined}
          onClick={onChange ? () => onChange(n) : undefined}
          data-testid={`${testidPrefix}-${n}`}
          className={onChange ? "transition-transform hover:scale-110" : ""}
        >
          <Star
            className={`${size} ${filled ? "fill-[#F59E0B] text-[#F59E0B]" : "text-gray-300"}`}
          />
        </Cmp>
      );
    })}
  </div>
);

const distribution = (reviews) => {
  const buckets = [0, 0, 0, 0, 0]; // index 0 = 1★, 4 = 5★
  reviews.forEach((r) => {
    if (r.rating >= 1 && r.rating <= 5) buckets[r.rating - 1] += 1;
  });
  return buckets;
};

export default function PartnerReviews({ partnerId, partnerName }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg: 0, count: 0 });
  const [my, setMy] = useState({ review: null, eligible: false, eligible_course_title: "" });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadPublic = async () => {
    try {
      // Reviews endpoint takes slug — the parent passes partnerId only; grab both from public partner endpoint
      // But we already have partnerId. We'll fetch via /partner-reviews/mine to know eligibility;
      // for public reviews we use the /partners/public/{slug}/reviews endpoint but we don't have slug here.
      // So the parent should pass slug too — we'll refactor to accept slug via prop instead.
    } catch (e) {
      // no-op
    }
  };

  // We'll accept `slug` prop too — this component is called with both
  const loadReviews = async (slug) => {
    const { data } = await api.get(`/api/partners/public/${slug}/reviews`);
    setData(data);
  };

  const loadMine = async () => {
    if (!user) { setMy({ review: null, eligible: false, eligible_course_title: "" }); return; }
    try {
      const { data } = await api.get(`/api/partner-reviews/mine/${partnerId}`);
      setMy(data);
      if (data.review) {
        setRating(data.review.rating);
        setComment(data.review.comment || "");
      }
    } catch { /* not fatal */ }
  };

  useEffect(() => {
    if (!partnerId) return;
    // The prop passes slug via arguments.props.slug; fallback: read from window.location
    const slug = window.location.pathname.split("/partners/")[1] || "";
    if (slug) loadReviews(slug);
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, user]);

  const submit = async () => {
    if (rating < 1 || rating > 5) return toast.error("Please pick a star rating");
    setBusy(true);
    try {
      await api.post("/api/partner-reviews", { partner_id: partnerId, rating, comment });
      toast.success(my.review ? "Review updated" : "Thanks — your review is live!");
      setShowForm(false);
      const slug = window.location.pathname.split("/partners/")[1] || "";
      await Promise.all([loadReviews(slug), loadMine()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  const dist = distribution(data.reviews);
  const pct = (n) => (data.count ? Math.round((n / data.count) * 100) : 0);

  return (
    <section className="max-w-5xl mx-auto py-12 px-6" data-testid="storefront-reviews">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display font-bold text-3xl">Student reviews</h2>
        {my.eligible && !showForm && (
          <Button data-testid="storefront-review-open-form-btn"
            onClick={() => setShowForm(true)}
            className="bg-[#DC2626] hover:bg-[#B91C1C]">
            {my.review ? "Edit your review" : "Leave a review"}
          </Button>
        )}
      </div>

      {/* Summary */}
      <Card className="mb-6">
        <CardContent className="p-6 grid md:grid-cols-3 gap-6 items-center">
          <div className="text-center md:text-left" data-testid="storefront-review-summary">
            <div className="text-5xl font-bold text-[#064E3B]">
              {data.count ? data.avg.toFixed(1) : "—"}
            </div>
            <StarRow value={Math.round(data.avg)} size="w-5 h-5" testidPrefix="summary-star" />
            <div className="text-sm text-gray-500 mt-1">
              {data.count} {data.count === 1 ? "review" : "reviews"}
            </div>
          </div>
          <div className="md:col-span-2 space-y-1">
            {[5, 4, 3, 2, 1].map((n) => (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-6 text-right">{n}★</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#F59E0B]" style={{ width: `${pct(dist[n - 1])}%` }} />
                </div>
                <span className="w-10 text-right text-gray-500">{dist[n - 1]}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Leave review CTA if not logged in / not eligible */}
      {!user && (
        <Card className="mb-6" data-testid="storefront-review-signin-cta">
          <CardContent className="p-4 text-sm text-gray-600 text-center">
            <a href="/login" className="text-[#DC2626] font-semibold hover:underline">Sign in</a>{" "}
            after completing a {partnerName} course to leave a review.
          </CardContent>
        </Card>
      )}
      {user && !my.eligible && (
        <Card className="mb-6" data-testid="storefront-review-not-eligible">
          <CardContent className="p-4 text-sm text-gray-600 text-center">
            Only students who have completed a {partnerName} course can leave a review.
          </CardContent>
        </Card>
      )}

      {/* Review form */}
      {showForm && my.eligible && (
        <Card className="mb-6" data-testid="storefront-review-form">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="text-sm font-semibold mb-2">Your rating</div>
              <StarRow value={rating} onChange={setRating} testidPrefix="form-star" />
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Your review (optional)</div>
              <Textarea rows={4} value={comment} maxLength={2000}
                data-testid="storefront-review-comment-input"
                placeholder={`How was your experience with ${partnerName}?`}
                onChange={(e) => setComment(e.target.value)} />
              <div className="text-[10px] text-gray-400 mt-1 text-right">
                {comment.length}/2000
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline"
                data-testid="storefront-review-cancel-btn"
                onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy}
                data-testid="storefront-review-submit-btn"
                className="bg-[#DC2626] hover:bg-[#B91C1C]">
                {busy ? "Submitting…" : my.review ? "Update review" : "Post review"}
              </Button>
            </div>
            {my.eligible_course_title && (
              <div className="text-xs text-gray-500">
                Verified completion: <b>{my.eligible_course_title}</b>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reviews list */}
      {data.reviews.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500"
          data-testid="storefront-reviews-empty">
          No reviews yet. Be the first — complete a course to leave one!
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <Card key={r.id} data-testid="storefront-review-item">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#064E3B] text-white grid place-items-center font-bold">
                      {r.user_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{r.user_name}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(r.created_at).toLocaleDateString("en-CA",
                          { year: "numeric", month: "short", day: "numeric" })}
                      </div>
                    </div>
                  </div>
                  <StarRow value={r.rating} size="w-4 h-4" testidPrefix={`item-star-${r.id}`} />
                </div>
                {r.comment && (
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{r.comment}</p>
                )}
                {r.course_title && (
                  <Badge variant="secondary" className="text-[10px] mt-3">
                    Verified · Completed {r.course_title}
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
